import React, { useState } from 'react';
import { Eye, Pencil, Clock, CheckCircle2, AlertCircle, X, ChevronLeft, ChevronRight, Image, Loader2, Camera, XCircle, CheckCircle } from 'lucide-react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import HelpButton from '../components/shared/HelpButton';
import { fetchTickets, updateTickets } from '../utils/api';
import { formatDateForDisplay } from '../utils/format';
import { useUser } from '../utils/UserContext';
import { cssUrl } from '../utils/cssVersion';

const filterTicketsStyles = cssUrl('filterTemplate.css');
// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const STATUS = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Ticket Completed',
};

const gradientText = {
  background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 50%, #8b5cf6 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  filter: 'drop-shadow(0 0 20px rgba(34,211,238,0.3))',
};

const statusMeta = (status) => {
  switch (status) {
    case STATUS.COMPLETED:
      return { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', icon: <CheckCircle2 size={13} /> };
    case STATUS.IN_PROGRESS:
      return { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', icon: <Clock size={13} /> };
    default:
      return { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', icon: <AlertCircle size={13} /> };
  }
};

const parseImages = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(i => (typeof i === 'object' ? i.url : i)).filter(Boolean);
  if (typeof raw === 'string') return raw.split(',').map(u => u.trim()).filter(Boolean);
  return [];
};

// ===== HELPER: Get last day of a month =====
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ─────────────────────────────────────────────
// Ticket Card
// ─────────────────────────────────────────────
const TicketCard = ({ item, onClick }) => {
  const status = item[7] || STATUS.PENDING;
  const meta = statusMeta(status);
  const isCompleted = status === STATUS.COMPLETED;

  return (
    <div
      onClick={() => onClick(item)}
      className="relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 hover:scale-[1.01] hover:shadow-xl"
      style={{
        background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(9,18,32,0.98) 100%)',
        border: `1px solid ${meta.color}40`,
        boxShadow: `0 4px 24px ${meta.color}15`,
      }}
    >
      {/* Glow strip */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, transparent, ${meta.color}, transparent)` }}
      />

      <div className="p-4 space-y-3">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-0.5">
              {item[2] || 'No Type'}
            </p>
            <h3 className="text-white font-semibold text-sm truncate">{item[9] || 'No Subject'}</h3>
            <p className="text-slate-400 text-xs mt-0.5 truncate">{item[1] || ''}</p>
          </div>
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0"
            style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}50` }}
          >
            {meta.icon}
            {status}
          </div>
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Created: {formatDateForDisplay(item[3]) || '—'}</span>
          {item[4] && <span>Done: {formatDateForDisplay(item[4])}</span>}
        </div>

        {/* Cleaner + action hint */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: 'rgba(34,211,238,0.15)', color: '#22d3ee', border: '1px solid #22d3ee40' }}
            >
              {(item[11] || '?')[0]?.toUpperCase()}
            </div>
            <span className="text-xs text-slate-400 truncate max-w-[120px]">{item[11] || 'Unassigned'}</span>
          </div>
          <div
            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
            style={{
              background: isCompleted ? 'rgba(34,211,238,0.08)' : 'rgba(139,92,246,0.12)',
              color: isCompleted ? '#22d3ee' : '#a78bfa',
              border: `1px solid ${isCompleted ? '#22d3ee30' : '#8b5cf630'}`,
            }}
          >
            {isCompleted ? <Eye size={11} /> : <Pencil size={11} />}
            <span>{isCompleted ? 'View' : 'Update'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Image Carousel (shared)
// ─────────────────────────────────────────────
const ImageCarousel = ({ images, title }) => {
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const safe = Math.min(idx, Math.max(images.length - 1, 0));
  const next = () => setIdx(p => (p === images.length - 1 ? 0 : p + 1));
  const prev = () => setIdx(p => (p === 0 ? images.length - 1 : p - 1));

  if (!images || images.length === 0) {
    return (
      <div>
        <label className="block text-sm font-medium mb-2" style={gradientText}>{title}</label>
        <div
          className="rounded-2xl flex items-center justify-center h-48 bg-gray-800/50"
          style={{ border: '1px solid #22d3ee20' }}
        >
          <div className="text-center">
            <Image size={28} className="mx-auto text-gray-600 mb-2" />
            <p className="text-gray-500 text-sm">No images available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium" style={gradientText}>{title}</label>
        {images.length > 1 && (
          <span
            className="px-2 py-0.5 rounded-full text-white text-xs font-medium"
            style={{ background: 'linear-gradient(135deg,#22d3ee,#3b82f6)', boxShadow: '0 0 10px rgba(34,211,238,0.3)' }}
          >
            {safe + 1} / {images.length}
          </span>
        )}
      </div>

      <div
        className="relative overflow-hidden rounded-2xl group"
        style={{ border: '1px solid #22d3ee', boxShadow: '0 8px 32px rgba(34,211,238,0.1)' }}
        onTouchStart={e => setTouchStart(e.targetTouches[0].clientX)}
        onTouchMove={e => setTouchEnd(e.targetTouches[0].clientX)}
        onTouchEnd={() => {
          const d = touchStart - touchEnd;
          if (d > 50) next();
          if (d < -50) prev();
        }}
      >
        <div className="h-72 flex items-center justify-center bg-gray-900/50">
          <img
            src={images[safe]}
            alt={`${title} ${safe + 1}`}
            className={`max-w-full max-h-full object-contain transition-all duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            onError={e => { e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzFmMjkzNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2Yjc1ODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBVbmF2YWlsYWJsZTwvdGV4dD48L3N2Zz4='; }}
          />
        </div>

        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300"
              style={{ width: 40, height: 40, background: 'rgba(17,24,39,0.9)', border: '1px solid #22d3ee', borderRadius: '50%' }}
            >
              <ChevronLeft size={20} className="mx-auto text-cyan-400" />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300"
              style={{ width: 40, height: 40, background: 'rgba(17,24,39,0.9)', border: '1px solid #22d3ee', borderRadius: '50%' }}
            >
              <ChevronRight size={20} className="mx-auto text-cyan-400" />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex justify-center mt-3 gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className="transition-all duration-300"
              style={{
                width: i === safe ? 24 : 8, height: 8, borderRadius: 4,
                background: i === safe ? 'linear-gradient(135deg,#22d3ee,#3b82f6)' : '#4b5563',
                boxShadow: i === safe ? '0 0 8px rgba(34,211,238,0.5)' : 'none',
              }}
            />
          ))}
        </div>
      )}
      <p className="text-center text-xs text-gray-500 mt-2">Swipe or use arrows to navigate</p>
    </div>
  );
};

// ─────────────────────────────────────────────
// View Ticket Modal (Completed)
// ─────────────────────────────────────────────
const ViewTicketModal = ({ item, onClose }) => {
  const beforeImages = parseImages(item[13]);
  const afterImages = parseImages(item[14]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4" style={{ top: 'var(--fr-modal-top, 0px)', paddingTop: 24, paddingBottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }}>
      <div
        className="bg-gray-900/95 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        style={{ border: '1px solid #22d3ee', maxHeight: 'min(90vh, calc(100dvh - var(--fr-modal-top, 0px) - 100px))' }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-bold flex items-center gap-2" style={gradientText}>
            <Eye size={22} />
            Ticket Details
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-cyan-400 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0, maxHeight: '60vh' }}>   {/* house rule: body scrolls, ≤ 60vh */}
          <div className="p-6 space-y-4">
            {[
              ['Job Type', item[2]],
              ['Client', item[1]],
              ['Created Date', formatDateForDisplay(item[3])],
              ['Completed Date', formatDateForDisplay(item[4])],
              ['Completion Time', item[5]],
              ['Client Approval', item[6]],
              ['Cleaner', item[11]],
              ['Subject', item[9]],
            ].map(([label, val]) => (
              <div key={label}>
                <label className="block text-sm font-medium mb-1" style={gradientText}>{label}</label>
                <input
                  type="text"
                  value={val || ''}
                  readOnly
                  className="w-full p-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none"
                />
              </div>
            ))}

            {/* Status badge */}
            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Status</label>
              <div
                className="w-full p-2 rounded-lg border text-white font-medium"
                style={{ ...statusMeta(item[7]).bg && { background: statusMeta(item[7]).bg }, borderColor: statusMeta(item[7]).color }}
              >
                {item[7] || ''}
              </div>
            </div>

            {/* Job Details */}
            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Job Details</label>
              <textarea value={item[10] || ''} readOnly rows="3"
                className="w-full p-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none resize-none" />
            </div>

            <ImageCarousel images={beforeImages} title="Before Photos" />

            {/* Cleaner Comments */}
            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Cleaner Comments</label>
              <textarea value={item[15] || ''} readOnly rows="3"
                className="w-full p-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none resize-none" />
            </div>

            <ImageCarousel images={afterImages} title="Finished Photos" />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black text-white font-medium hover:shadow-lg flex items-center gap-2 transition-all"
            style={{ border: '1px solid #22d3ee', boxShadow: '0 0 20px rgba(34,211,238,0.2)' }}
          >
            <X size={16} /> Close Ticket
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Update Ticket Modal (Pending / In Progress)
// ─────────────────────────────────────────────
const UpdateTicketModal = ({ item, onClose, onSubmit, isSubmitting, setToast }) => {
  const { username } = useUser();
  const [imagePreview, setImagePreview] = useState([]);

  // Created date is the anchor (item[3])
  const createdDate = item[3] || '';

  const [formData, setFormData] = useState({
    company: item[1] || '',
    requestType: item[2] || '',
    dateComplete: item[4] || '',
    completeTime: item[5] || '',
    statusApproval: item[7] || STATUS.PENDING,
    cleaner: item[11] || username || '',
    subjectTitle: item[9] || '',
    jobDetails: item[10] || '',
    cleanerComments: item[15] || '',
    cleanerPhotos: [],
  });

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
  };

  // ─────────────────────────────────────────────
  //  COMPLETED DATE HANDLER — anchor-based
  // ─────────────────────────────────────────────
  //
  //  Rules:
  //  • Created date (item[3]) is the anchor.
  //  • Completed date cannot be before created date → toast + reject.
  //  • Completed date must be in the same month and year as created date
  //    → clamp to EOM of created date's month with a toast.
  //
  const handleCompletedDateChange = (val) => {
    if (!val) {
      setFormData(p => ({ ...p, dateComplete: '' }));
      return;
    }

    if (!createdDate) {
      // No anchor available — accept as-is
      setFormData(p => ({ ...p, dateComplete: val }));
      return;
    }

    // Floor: completed cannot be before created
    if (val < createdDate) {
      if (setToast) {
        setToast({
          message: 'Completed date cannot be before the created date.',
          type: 'warning',
        });
      }
      return;
    }

    // Ceiling: completed must stay in the same month and year as created
    const [cy, cm] = createdDate.split('-').map(Number);
    const [vy, vm] = val.split('-').map(Number);
    if (cy !== vy || cm !== vm) {
      const lastDay = getLastDayOfMonth(cy, cm - 1);
      const pad = (n) => String(n).padStart(2, '0');
      const clamped = `${cy}-${pad(cm)}-${pad(lastDay)}`;
      if (setToast) {
        setToast({
          message: `Completed date must be in the same month as the created date. Clamped to ${lastDay}th.`,
          type: 'warning',
        });
      }
      setFormData(p => ({ ...p, dateComplete: clamped }));
      return;
    }

    setFormData(p => ({ ...p, dateComplete: val }));
  };

  // Defence-in-depth: re-check on submit
  const validateOnSubmit = () => {
    const { dateComplete } = formData;
    if (!dateComplete || !createdDate) return true;

    if (dateComplete < createdDate) {
      if (setToast) setToast({ message: 'Completed date cannot be before the created date.', type: 'warning' });
      return false;
    }

    const [cy, cm] = createdDate.split('-').map(Number);
    const [vy, vm] = dateComplete.split('-').map(Number);
    if (cy !== vy || cm !== vm) {
      if (setToast) setToast({ message: 'Completed date must be in the same month and year as the created date.', type: 'warning' });
      return false;
    }

    return true;
  };

  const handleImageUpload = e => {
    const files = Array.from(e.target.files);
    const newPreviews = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push({ url: reader.result, name: file.name });
        if (newPreviews.length === files.length) {
          setImagePreview(p => [...p, ...newPreviews]);
          setFormData(p => ({ ...p, cleanerPhotos: [...p.cleanerPhotos, ...files] }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = idx => {
    setImagePreview(p => p.filter((_, i) => i !== idx));
    setFormData(p => ({ ...p, cleanerPhotos: p.cleanerPhotos.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = () => {
    if (!validateOnSubmit()) return;

    const fd = new FormData();
    fd.append('recordids', item[0]);
    fd.append('company', formData.company.toUpperCase());
    fd.append('requesttype', formData.requestType);
    fd.append('datecomplete', formData.dateComplete);
    fd.append('completetime', formData.completeTime);
    fd.append('statusapproval', formData.statusApproval);
    fd.append('cleaner', formData.cleaner || '');
    fd.append('subjecttitle', formData.subjectTitle);
    fd.append('jobdetails', formData.jobDetails);
    fd.append('cleanercomments', formData.cleanerComments || '');
    formData.cleanerPhotos.forEach(file => fd.append('cleanerPhotos[]', file));
    onSubmit(fd);
  };

  const inputClass = "w-full p-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-400 focus:outline-none";
  const readonlyClass = "w-full p-2 rounded-lg bg-gray-800/60 text-white border border-gray-700 focus:outline-none cursor-not-allowed";

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4" style={{ top: 'var(--fr-modal-top, 0px)', paddingTop: 24, paddingBottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }}>
      <div
        className="bg-gray-900/95 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        style={{ border: '1px solid #22d3ee', maxHeight: 'min(90vh, calc(100dvh - var(--fr-modal-top, 0px) - 100px))' }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-bold flex items-center gap-2" style={gradientText}>
            <Pencil size={22} />
            Update Ticket
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-cyan-400 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0, maxHeight: '60vh' }}>   {/* house rule: body scrolls, ≤ 60vh */}
          <div className="p-6 space-y-4">

            {/* Readonly fields */}
            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Client</label>
              <input type="text" value={formData.company.toUpperCase()} readOnly className={readonlyClass} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Job Request Type</label>
              <input type="text" name="requestType" value={formData.requestType} readOnly className={readonlyClass} />

            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Subject</label>
              <input type="text" name="subjectTitle" value={formData.subjectTitle} readOnly className={readonlyClass} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Job Request Details</label>
              <textarea name="jobDetails" value={formData.jobDetails} readOnly rows="3" className={`${readonlyClass} resize-none`} />
            </div>
            <ImageCarousel images={parseImages(item[13])} title="Before Photos" />
            <ImageCarousel images={parseImages(item[14])} title="Existing Cleaner Photos" />
            {/* Editable fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={gradientText}>Created Date</label>
                <input type="date" value={createdDate} readOnly className={`${readonlyClass} [color-scheme:dark]`} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={gradientText}>Completed Date</label>
                <input
                  type="date"
                  name="dateComplete"
                  value={formData.dateComplete}
                  onChange={(e) => handleCompletedDateChange(e.target.value)}
                  className={`${inputClass} [color-scheme:dark]`}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Completed Time</label>
              <input type="text" name="completeTime" value={formData.completeTime} readOnly className={readonlyClass} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Cleaner Attending</label>
              <input type="text" name="cleaner" value={formData.cleaner} onChange={handleChange}
                placeholder="No-Data" className={inputClass} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Workload Status</label>
              <select name="statusApproval" value={formData.statusApproval} onChange={handleChange} className={inputClass}>
                <option>{STATUS.PENDING}</option>
                <option>{STATUS.IN_PROGRESS}</option>
                <option>{STATUS.COMPLETED}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={gradientText}>Cleaner Comments</label>
              <textarea name="cleanerComments" value={formData.cleanerComments} onChange={handleChange}
                rows="3" className={`${inputClass} resize-none`} />
            </div>

            {/* Photo Upload */}
            <div>
              <label className="block text-sm font-medium mb-3" style={gradientText}>Add Cleaner Photos</label>

              {imagePreview.length > 0 && (
                <div className="relative p-4 rounded-2xl bg-gray-800/50 border border-gray-700 mb-4">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {imagePreview.map((preview, i) => (
                      <div key={i} className="relative group">
                        <div className="relative overflow-hidden rounded-xl transform transition-all hover:scale-105">
                          <img src={preview.url} alt={preview.name} className="w-full h-24 object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <button
                            type="button"
                            onClick={() => removeImage(i)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 scale-0 group-hover:scale-100 transition-all hover:bg-red-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-sm font-medium text-cyan-400">
                    {imagePreview.length} {imagePreview.length === 1 ? 'photo' : 'photos'} selected
                  </p>
                </div>
              )}

              <div className="relative group">
                <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" id="photo-upload-ft" />
                <label
                  htmlFor="photo-upload-ft"
                  className="flex flex-col items-center justify-center p-8 cursor-pointer rounded-2xl transition-all duration-300 hover:scale-[1.01]"
                  style={{ background: 'linear-gradient(135deg,rgba(17,24,39,1),rgba(9,18,32,1))', border: '1px solid #22d3ee', boxShadow: '0 8px 24px rgba(34,211,238,0.15)' }}
                >
                  <div className="w-16 h-16 bg-cyan-400/10 rounded-full flex items-center justify-center border border-cyan-400/30 mb-3">
                    <Camera size={28} className="text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-bold mb-1" style={gradientText}>Upload Photos</h3>
                  <p className="text-gray-400 text-sm">JPG, PNG, GIF · Max 10MB · Up to 10 files</p>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 flex justify-end gap-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black text-white font-medium hover:shadow-lg flex items-center gap-2 transition-all"
            style={{ border: '1px solid #22d3ee' }}
          >
            <XCircle size={16} /> Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-black text-white font-medium hover:shadow-lg flex items-center gap-2 transition-all group disabled:opacity-50"
            style={{ border: '1px solid #22d3ee', boxShadow: '0 0 20px rgba(34,211,238,0.2)' }}
          >
            {isSubmitting
              ? <><Loader2 size={16} className="animate-spin" /> Submitting...</>
              : <><CheckCircle size={16} className="group-hover:text-cyan-400 transition-colors" /><span className="group-hover:text-cyan-400 transition-colors">Submit Update</span></>
            }
          </button>
        </div>
      </div>
    </div>
  );
};


const StatusChips = (chipQuery, setChipQuery, chipsOpen, setChipsOpen) => {
  const statuses = [
    { label: 'Pending', value: 'pending', color: '#fbbf24' },
    { label: 'In Progress', value: 'in progress', color: '#fbbf24' },
    { label: 'Completed', value: 'ticket completed', color: '#fbbf24' },
    { label: 'Today', value: 'today', color: '#fbbf24' },
  ];

  const activeTerms = chipQuery
    ? chipQuery.toLowerCase().split(',').map(t => t.trim()).filter(Boolean)
    : [];

  const toggle = (val) => {
    if (activeTerms.includes(val)) {
      const updated = activeTerms.filter(t => t !== val);
      setChipQuery(updated.join(', '));
    } else {
      const updated = [...activeTerms, val];
      setChipQuery(updated.join(', '));
    }
  };

  return (
    <div className="px-4 pb-2">
      <div className="flex items-center gap-2 flex-wrap">
        {statuses.map(({ label, value, color }) => {
          const active = activeTerms.includes(value);
          return (
            <button
              key={value}
              onClick={() => toggle(value)}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200"
              style={{
                background: active ? `${color}22` : 'rgba(255,255,255,0.04)',
                color: active ? color : '#94a3b8',
                border: `1px solid ${active ? color + '60' : color + '90'}`,
                boxShadow: active ? `0 0 10px ${color}30` : 'none',
              }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: active ? color : '#6b7280' }}
              />
              {label}
              {active && <X size={10} />}
            </button>
          );
        })}

        {activeTerms.length > 0 && (
          <button
            onClick={() => setChipQuery('')}
            className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-gray-500 hover:text-gray-300 transition-colors"
            style={{ border: '1px solid #374151' }}
          >
            <X size={10} /> Clear All
          </button>
        )}
      </div>
    </div>
  );
};





const FilterTickets = () => {
  // renderModal decides which modal to show based on ticket status
  const renderModal = (item, onClose, onSubmit, isSubmitting, setToast) => {
    const status = item[7];
    if (status === STATUS.COMPLETED) {
      return <ViewTicketModal item={item} onClose={onClose} />;
    }
    return (
      <UpdateTicketModal
        item={item}
        onClose={onClose}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        setToast={setToast}
      />
    );
  };

  return (
    <FilterTemplate
      title="Tickets"
      helpButton={
      <HelpButton helpData={[
        { chip: 'tickets_filter', urlFilter: '/static/pdffilters/TicketFilterGuide.pdf' }
      ]} />
    }
      subtitle="Service Tickets"
      backRoute="/profile"
      searchPlaceholder="Search tickets..."
      stylesheetHref={filterTicketsStyles}
      fetchData={fetchTickets}
      dataKey="tickets"
      searchFields={[1, 2, 7, 9, 11]}
      chipLogic="or"
      todayFilterIndex={3}
      renderCard={(item, onClick) => <TicketCard item={item} onClick={onClick} />}
      renderModal={renderModal}
      onSubmit={updateTickets}
      emptyIcon="🎫"
      emptyTitle="No Tickets Found"
      loadingMessage="Loading tickets..."
      showNavigation={true}
      showHeader={false}
      renderChips={StatusChips}
      
      />
  );
};

export default FilterTickets;